import {
    CategoryLegalConfig,
    CategoryLegalConfigDetails,
    ContentDocument,
} from '@/lib-backend';

/**
 * The seven documents of the Legal config axis, in display order. Keys match
 * the field names of CategoryLegalConfig / CategoryLegalConfigDetails /
 * CategoryLegalConfigUpdate (each update field is `${key}Override`).
 */
export const LEGAL_DOC_KEYS = [
    'TermsOfService',
    'PrivacyPolicy',
    'AmlPolicy',
    'CookiesPolicy',
    'RefundPolicy',
    'Disclaimer',
    'AboutDescription',
] as const;

export type LegalDocKey = (typeof LEGAL_DOC_KEYS)[number];

export const LEGAL_DOC_LABELS: Record<LegalDocKey, string> = {
    TermsOfService: 'Terms of Service',
    PrivacyPolicy: 'Privacy Policy',
    AmlPolicy: 'AML/CFT Policy',
    CookiesPolicy: 'Cookies Policy',
    RefundPolicy: 'Refund Policy',
    Disclaimer: 'Disclaimer',
    AboutDescription: 'About Description',
};

/**
 * Liquid variables the backend substitutes into legal documents at serve time
 * (platform-server TemplateHelper.CreateLegalGlobals). Single source of truth
 * for the editor hint and the preview validation.
 */
export const LEGAL_TEMPLATE_VARIABLES: {
    token: string;
    description: string;
}[] = [
    {
        token: 'Realm.Domain',
        description: 'Realm host, e.g. brand.example.com',
    },
    { token: 'Company.Name', description: 'Legal company name' },
    { token: 'Company.Address', description: 'Company address' },
    { token: 'Company.CountryCode', description: 'ISO country code, e.g. CY' },
    {
        token: 'Company.RegistrationDetails',
        description: 'Registration details / number',
    },
    {
        token: 'Branding.DisplayName',
        description: 'Public platform name (used in legal copy)',
    },
    { token: 'Branding.Name', description: 'Internal brand name' },
    {
        token: 'Support.SupportEmail',
        description: 'Support email; empty string when not set',
    },
];

export function emptyContentDocument(): ContentDocument {
    return { Title: '', Content: '' };
}

export function emptyLegalConfig(): CategoryLegalConfig {
    return {
        TermsOfService: emptyContentDocument(),
        PrivacyPolicy: emptyContentDocument(),
        AmlPolicy: emptyContentDocument(),
        CookiesPolicy: emptyContentDocument(),
        RefundPolicy: emptyContentDocument(),
        Disclaimer: emptyContentDocument(),
        AboutDescription: emptyContentDocument(),
    };
}

/** Per-document realm-level draft: override flag + the document being edited. */
export type LegalDocDraft = { overridden: boolean; doc: ContentDocument };

export type LegalOverridesDraft = Record<LegalDocKey, LegalDocDraft>;

export function emptyLegalDraft(): LegalOverridesDraft {
    const entry = (): LegalDocDraft => ({
        overridden: false,
        doc: emptyContentDocument(),
    });
    return {
        TermsOfService: entry(),
        PrivacyPolicy: entry(),
        AmlPolicy: entry(),
        CookiesPolicy: entry(),
        RefundPolicy: entry(),
        Disclaimer: entry(),
        AboutDescription: entry(),
    };
}

/** Seed the realm-level draft from the authoritative inheritance details. */
export function legalDraftFromDetails(
    details: CategoryLegalConfigDetails,
): LegalOverridesDraft {
    const entry = (key: LegalDocKey): LegalDocDraft => ({
        overridden: details[key].IsOverridden,
        doc: { ...details[key].Effective },
    });
    return {
        TermsOfService: entry('TermsOfService'),
        PrivacyPolicy: entry('PrivacyPolicy'),
        AmlPolicy: entry('AmlPolicy'),
        CookiesPolicy: entry('CookiesPolicy'),
        RefundPolicy: entry('RefundPolicy'),
        Disclaimer: entry('Disclaimer'),
        AboutDescription: entry('AboutDescription'),
    };
}
